import { Medications } from "../services/stripe";
import type { SurveySession } from "../typings";

type ResolveMedicationForResultsOptions = {
  surveySessionData?: Partial<SurveySession> | null;
  medication?: string | null;
  preferMedication?: boolean;
};

const MEDICATION_VALUES = new Set<string>(Object.values(Medications));

const normalizeMedicationInput = (value?: string | null) =>
  value?.trim() || undefined;

const canonicalizeMedicationValue = (
  value?: string | null
): Medications | undefined => {
  const normalized = normalizeMedicationInput(value);
  if (!normalized || !MEDICATION_VALUES.has(normalized)) {
    return undefined;
  }

  return normalized as Medications;
};

const getPreferredMedicationAnswerValue = (
  answer: unknown
): string | undefined => {
  if (typeof answer === "string") {
    return normalizeMedicationInput(answer);
  }

  if (answer && typeof answer === "object" && "medicationName" in answer) {
    const medicationName = (answer as { medicationName?: unknown })
      .medicationName;
    if (typeof medicationName === "string") {
      return normalizeMedicationInput(medicationName);
    }
  }

  if (answer && typeof answer === "object" && "answer" in answer) {
    const answerValue = (answer as { answer?: unknown }).answer;
    if (Array.isArray(answerValue)) {
      return normalizeMedicationInput(
        answerValue.find((value): value is string => typeof value === "string")
      );
    }

    if (typeof answerValue === "string") {
      return normalizeMedicationInput(answerValue);
    }
  }

  return undefined;
};

export const resolveMedicationForResults = ({
  surveySessionData,
  medication,
  preferMedication = false,
}: ResolveMedicationForResultsOptions): Medications | undefined => {
  const resolvedMedication = canonicalizeMedicationValue(medication);
  if (preferMedication && resolvedMedication) {
    return resolvedMedication;
  }

  const userMedication = canonicalizeMedicationValue(
    surveySessionData?.user?.medication
  );
  if (userMedication) {
    return userMedication;
  }

  const preferredMedicationAnswer = getPreferredMedicationAnswerValue(
    surveySessionData?.answers?.preferred_medication_name
  );
  const answerMedication = canonicalizeMedicationValue(
    preferredMedicationAnswer
  );
  if (answerMedication) {
    return answerMedication;
  }

  return resolvedMedication;
};

/**
 * List of medications that should redirect to checkout service results page
 * instead of local results page when approved.
 * Includes compound medications and other medications requiring checkout service routing.
 */
export const CHECKOUT_REDIRECT_MEDICATIONS: readonly Medications[] = [
  Medications.Sema, // "smd-sema"
  Medications.TirzMicroPill, // "smd-tirz-micro-pill"
  Medications.TirzMicroInj, // "smd-tirz-micro-inj"
  Medications.SemaPill, // "smd-sema-pill"
  Medications.TirzInj, // "smd-tirz-inj"
  Medications.TirzPill, // "smd-tirz-pill"
  Medications.SemaTirzDiscount, // "smd-sema-tirz-discount"
  Medications.SemaTirz, // "ol-sema-tirz"
  Medications.OLSemaInj, // "ol-sema-inj"
  Medications.OLTirzInj, // "ol-tirz-inj"
  Medications.OLSemaPill, // "ol-sema-pill"
  Medications.OLTirzPill, // "ol-tirz-pill"
  Medications.CVWegovyPill, // "cv-wego-pill"
  Medications.WegovyInj, // "wegovy-inj"
  Medications.CVSema, // "cv-sema"
  Medications.CVSemaPill, // "cv-sema-pill"
  Medications.CVMicroSema, // "cv-micro-sema"
  Medications.WegovyPill, // "wego-pill"
  Medications.FoundayoPill, // "foundayo-pill"
  Medications.ZepboundKwikpen, // "zepbound-kwikpen"
] as const;

/**
 * Checks if a medication should redirect to checkout service results page
 * instead of local results page
 *
 * @param medication - The medication enum value to check
 * @returns true if the medication should redirect to checkout service, false otherwise
 */
export const shouldRedirectToCheckout = (
  medication: Medications | string
): boolean => {
  return CHECKOUT_REDIRECT_MEDICATIONS.includes(medication as Medications);
};

/**
 * Checks if medication instructions should be shown for a given medication.
 * Wegovy tablets and Foundayo don't need injection-based medication instructions.
 *
 * @param medicationName - The medication name from Firebase
 * @returns true if medication instructions should be shown, false otherwise
 */
export const shouldShowMedicationInstructions = (
  medicationName: string | undefined
): boolean => {
  if (!medicationName) return true;
  const lowerName = medicationName.toLowerCase();
  // Wegovy tablets and Foundayo don't need injection-based medication instructions
  const isWegovyTablet =
    lowerName.includes("wegovy") && lowerName.includes("tablet");
  const isFoundayo = lowerName.includes("foundayo");
  return !isWegovyTablet && !isFoundayo;
};

/**
 * Returns a user-friendly display name for a given medication
 *
 * @param medication - The medication enum value
 * @returns A human-readable medication name
 */
export const getFriendlyMedicationName = (medication: Medications): string => {
  switch (medication) {
    case Medications.Sema:
    case Medications.SemaTirzDiscount:
    case Medications.SemaTirz:
    case Medications.OLSemaInj:
    case Medications.CVMicroSema:
      return "GLP-1 Injections";
    case Medications.SemaPill:
    case Medications.OLSemaPill:
    case Medications.CVSemaPill:
      return "GLP-1 Pills";
    case Medications.TirzInj:
    case Medications.OLTirzInj:
      return "GLP-1 + GIP Injections";
    case Medications.TirzPill:
    case Medications.OLTirzPill:
      return "GLP-1 + GIP Pills";
    case Medications.TirzMicroInj:
      return "GLP-1 Microdose Injections";
    case Medications.TirzMicroPill:
      return "GLP-1 + GIP Microdose Pills";
    case Medications.Ozempic:
      return "Ozempic®";
    case Medications.Mounjaro:
    case Medications.MounjaroInsurance:
      return "Mounjaro®";
    case Medications.SemaMonthly:
      return "Semaglutide Pill";
    case Medications.Zepbound:
      return "Zepbound®";
    case Medications.ZepboundKwikpen:
      return "Zepbound® KwikPen®";
    case Medications.Wegovy:
    case Medications.WegovyInj:
      return "Wegovy®";
    case Medications.WegovyPill:
    case Medications.CVWegovyPill:
      return "Wegovy® Pill";
    case Medications.FoundayoPill:
      return "Foundayo™ Pill";
    default:
      return "Medication";
  }
};
